/**
 * VerificationStatusCard — surfaces the employer's own verification
 * status on their dashboard (iter-135). Redacts reviewer identities and
 * internal notes.
 */
import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const COPY = {
    pending: {
        Icon: ShieldQuestion, tone: "bg-slate-50 border-slate-200 text-slate-800",
        title: "Verification in progress",
        body: "Typically reviewed within 2 business days. You can save postings as drafts while we complete this.",
    },
    info_requested: {
        Icon: ShieldAlert, tone: "bg-amber-50 border-amber-200 text-amber-900",
        title: "We need a bit more info",
        body: "Please respond to our email to finish verification. Drafts still work.",
    },
    approved: {
        Icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
        title: "Verified — you can now publish jobs",
        body: null,
    },
    rejected: {
        Icon: AlertTriangle, tone: "bg-rose-50 border-rose-200 text-rose-900",
        title: "Account not verified",
        body: null,
    },
    suspended: {
        Icon: AlertTriangle, tone: "bg-orange-50 border-orange-200 text-orange-900",
        title: "Account suspended",
        body: null,
    },
};

export default function VerificationStatusCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/employer/verification/status")
            .then(({ data }) => setData(data))
            .catch(() => {});
    }, []);
    if (!data) return null;
    const copy = COPY[data.status] || COPY.pending;
    const { Icon } = copy;
    return (
        <div className={`rounded-2xl border p-5 ${copy.tone}`} data-testid="employer-verification-status-card">
            <div className="flex items-start gap-3">
                <Icon size={22} className="shrink-0 mt-0.5" />
                <div className="flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide opacity-80">Account status</div>
                    <div className="font-heading text-xl font-semibold mt-0.5">{copy.title}</div>
                    {copy.body && <p className="text-sm mt-1">{copy.body}</p>}
                    {data.status === "info_requested" && (data.needed_items?.length > 0) && (
                        <ul className="mt-2 text-sm list-disc list-inside">
                            {data.needed_items.map((n) => <li key={n}>{n}</li>)}
                        </ul>
                    )}
                    {data.status === "rejected" && data.rejected_reason && (
                        <p className="text-sm mt-1"><strong>Reason:</strong> {data.rejected_reason}</p>
                    )}
                    {data.status === "suspended" && data.suspended_reason && (
                        <p className="text-sm mt-1"><strong>Reason:</strong> {data.suspended_reason}</p>
                    )}
                    {data.status === "approved" && data.trust_tier === 1 && data.post_review_remaining > 0 && (
                        <p className="text-sm mt-1">
                            Your next {data.post_review_remaining} posting{data.post_review_remaining === 1 ? "" : "s"} will pass a quick admin review before going live.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
